/**
 * Pourquoi le grand livre et les vues actuelles ne disent pas la même chose.
 *
 * ## Ce que ce module nomme
 *
 * `v_controle_du_grand_livre` rend des FAITS : quatre nombres, et l'écart
 * qu'ils doivent expliquer. Elle ne conclut pas. C'est ici que l'écart devient
 * une phrase — et ici seulement, pour qu'un écran, un export et un courriel ne
 * puissent pas en donner trois lectures différentes.
 *
 * ## L'identité, et pourquoi elle est vérifiée deux fois
 *
 *     écart = factures annulées non créditées     (+)
 *           - notes de crédit excédentaires       (-)
 *           + le même euro compté deux fois       (+)
 *           - notes de crédit sans facture        (-)
 *
 * Elle est vraie sur les 1 505 familles de la production, sans une exception,
 * et la migration refuse de s'installer si elle tombe en défaut. Ce module la
 * revérifie quand même, ligne par ligne : la migration ne contrôle que
 * l'instant de son application, alors que les données bougent tous les jours.
 * Un reste inexpliqué ne fait pas planter l'écran — il s'AFFICHE, parce qu'un
 * cinquième motif que personne n'a nommé est exactement ce qu'il faut voir.
 *
 * ## Ce qu'un écart n'est pas
 *
 * Ce n'est pas une erreur de saisie, et ce n'est pas un bogue à corriger dans
 * les vues. Trois des quatre motifs sont des **décisions comptables en
 * attente** : une facture annulée qu'aucune note de crédit n'a éteinte reste
 * une dette tant qu'on n'a pas tranché entre la créditer et la mettre en
 * perte. Le grand livre ne fait que refuser de l'effacer en silence.
 *
 * ## Et ce qu'un solde à zéro ne prouve pas
 *
 * Au 19 septembre 2026, **589 virements sortants attendent encore d'être
 * placés**, dont 45 rattachables à 39 familles. Pour celles-là, le grand livre
 * croit encore devoir un argent déjà parti en banque. `provisoire` est vrai
 * dans ce cas, et un écran qui ne le dirait pas présenterait un provisoire
 * comme un définitif.
 */

import { euros } from '../finances/situation';

/** Les faits, tels que `v_controle_du_grand_livre` les rend. */
export interface FaitsDeLEcart {
  userId: string;
  prenom: string | null;
  nom: string | null;
  email: string | null;
  /** La somme des mouvements du grand livre. Positif : la famille doit. */
  soldeCents: number;
  mouvements: number;
  /** Ce que les vues actuelles disent du même compte. */
  soldeDesVuesCents: number;
  ecartCents: number;
  annuleesNonCrediteesCents: number;
  notesExcedentairesCents: number;
  memeEuroDeuxFoisCents: number;
  notesSansFactureCents: number;
  avoirCents: number;
  renduSurNotesCents: number;
  sortiesEnFile: number;
  sortiesEnFileCents: number;
}

export const MOTIFS = [
  'facture_annulee_non_creditee',
  'meme_euro_deux_fois',
  'note_excedentaire',
  'note_sans_facture',
] as const;
export type CodeDeMotif = (typeof MOTIFS)[number];

export interface Motif {
  label: string;
  /** Ce qui s'est passé, en une phrase. */
  explication: string;
  /** Ce qu'il reste à décider. Vide quand rien n'est à décider. */
  decision: string;
  /** Le signe que ce motif apporte à l'écart. */
  signe: 1 | -1;
}

export const LIBELLES_DE_MOTIF: Record<CodeDeMotif, Motif> = {
  facture_annulee_non_creditee: {
    /*
      « Jamais créditée » était faux pour une des quatre familles :
      CDV-CSSA-2500539 vaut 95,00 €, une note de 50,00 € existe déjà, et il
      reste 45,00 €. Le motif porte sur ce qui RESTE, pas sur l'absence de
      note — et se tromper là-dessus ferait émettre 95,00 € au lieu de 45,00 €,
      c'est-à-dire créerait le motif d'à côté.
    */
    label: 'Facture annulée, pas entièrement créditée',
    explication:
      "La facture a été annulée sans qu'une note de crédit l'éteigne en " +
      "entier. Les vues la laissent tomber dès l'annulation ; le grand livre " +
      'garde la dette tant que rien ne l\u2019a éteinte.',
    decision: 'À trancher : créditer le reste, ou le mettre en perte.',
    signe: 1,
  },
  meme_euro_deux_fois: {
    label: 'Le même euro compté deux fois',
    explication:
      "L'argent est arrivé en provision et il est reparti sur une note de " +
      "crédit. Les deux écritures sont justes, mais rien ne les reliait : " +
      "l'avoir restait entier alors qu'il avait déjà été rendu.",
    decision: 'Rien à décider : le grand livre les annule déjà l’un par l’autre.',
    signe: 1,
  },
  note_excedentaire: {
    label: 'Facture créditée au-delà de son montant',
    explication:
      "Les notes de crédit dépassent le montant de la facture. " +
      "`outstanding_cents` plancherait à zéro et perd la différence ; le " +
      'grand livre la garde au crédit de la famille.',
    decision: 'À vérifier : une note de crédit a probablement été émise deux fois.',
    signe: -1,
  },
  note_sans_facture: {
    label: 'Note de crédit sans facture',
    explication:
      "Cette note ne désigne aucune facture. Elle n'entrait donc dans aucun " +
      'solde — ni par la facture, ni par la provision.',
    decision: 'À rattacher à sa facture, ou à confirmer comme avoir libre.',
    signe: -1,
  },
};

/** Un motif, avec ce qu'il pèse dans l'écart de CETTE famille. */
export interface MotifChiffre {
  code: CodeDeMotif;
  motif: Motif;
  /** Toujours positif : le montant du fait. */
  montantCents: number;
  /** Ce qu'il apporte à l'écart, signe compris. */
  apportCents: number;
}

export interface LectureDeLEcart {
  ecartCents: number;
  /** Les motifs qui pèsent, du plus lourd au plus léger. */
  motifs: MotifChiffre[];
  /** Ce que les motifs expliquent. Égale `ecartCents` quand tout est nommé. */
  expliqueCents: number;
  /**
   * Ce qu'aucun motif connu n'explique. Zéro sur toute la production du
   * 19 septembre 2026 — et affiché, jamais tu, s'il cesse de l'être.
   */
  resteInexpliqueCents: number;
  /** Vrai quand un virement sortant attend encore : le solde est provisoire. */
  provisoire: boolean;
  /** L'écart en une phrase, telle qu'un écran peut l'afficher sans rien ajouter. */
  phrase: string;
}

/**
 * Nomme l'écart d'une famille.
 *
 * Les motifs sont classés par ce qu'ils PÈSENT, pas par leur ordre de
 * déclaration : sur une famille qui en cumule deux, c'est le plus lourd qui
 * explique le chiffre affiché.
 */
export function lireLEcart(faits: FaitsDeLEcart): LectureDeLEcart {
  const bruts: Array<{ code: CodeDeMotif; montantCents: number }> = [
    { code: 'facture_annulee_non_creditee', montantCents: faits.annuleesNonCrediteesCents },
    { code: 'meme_euro_deux_fois', montantCents: faits.memeEuroDeuxFoisCents },
    { code: 'note_excedentaire', montantCents: faits.notesExcedentairesCents },
    { code: 'note_sans_facture', montantCents: faits.notesSansFactureCents },
  ];

  const motifs: MotifChiffre[] = bruts
    .filter((b) => b.montantCents !== 0)
    .map((b) => {
      const motif = LIBELLES_DE_MOTIF[b.code];
      return {
        code: b.code,
        motif,
        montantCents: b.montantCents,
        apportCents: b.montantCents * motif.signe,
      };
    })
    .sort((a, b) => Math.abs(b.apportCents) - Math.abs(a.apportCents));

  const expliqueCents = motifs.reduce((t, m) => t + m.apportCents, 0);
  const resteInexpliqueCents = faits.ecartCents - expliqueCents;
  const provisoire = faits.sortiesEnFile > 0;

  return {
    ecartCents: faits.ecartCents,
    motifs,
    expliqueCents,
    resteInexpliqueCents,
    provisoire,
    phrase: phraseDeLEcart(faits, motifs, resteInexpliqueCents, provisoire),
  };
}

function phraseDeLEcart(
  faits: FaitsDeLEcart,
  motifs: MotifChiffre[],
  resteCents: number,
  provisoire: boolean,
): string {
  const ecart = euros(Math.abs(faits.ecartCents));
  const sens =
    faits.ecartCents > 0
      ? `le grand livre réclame ${ecart} de plus que la situation financière`
      : `le grand livre réclame ${ecart} de moins que la situation financière`;

  let phrase: string;
  if (motifs.length === 0) {
    phrase = `${sens}, et aucun motif connu ne l'explique.`;
  } else if (resteCents !== 0) {
    phrase =
      `${sens}. ${nommerLesMotifs(motifs)} — ` +
      `et ${euros(Math.abs(resteCents))} qu'aucun motif connu n'explique.`;
  } else {
    phrase = `${sens}. ${nommerLesMotifs(motifs)}.`;
  }

  if (provisoire) {
    phrase +=
      ` Ce solde reste provisoire : ${pluriel(faits.sortiesEnFile, 'virement sortant', 'virements sortants')}` +
      ` ${faits.sortiesEnFile > 1 ? 'attendent' : 'attend'} encore d'être placé${faits.sortiesEnFile > 1 ? 's' : ''}` +
      ` (${euros(faits.sortiesEnFileCents)}).`;
  }
  return phrase;
}

function nommerLesMotifs(motifs: MotifChiffre[]): string {
  const parts = motifs.map(
    (m) => `${m.motif.label.toLowerCase()} : ${euros(m.montantCents)}`,
  );
  if (parts.length === 1) return capitaliser(parts[0]);
  return capitaliser(parts.slice(0, -1).join(', ') + ' et ' + parts[parts.length - 1]);
}

/**
 * Ce que l'ensemble des écarts représente, pour l'en-tête de l'écran.
 *
 * Le total est en VALEUR ABSOLUE : deux écarts de signes opposés ne
 * s'annulent pas, ce sont deux dossiers à traiter, pas zéro.
 */
export interface BilanDesEcarts {
  familles: number;
  totalAbsoluCents: number;
  /** Combien de familles chaque motif touche, et pour combien. */
  parMotif: Array<{ code: CodeDeMotif; motif: Motif; familles: number; montantCents: number }>;
  /** Les familles dont un motif au moins échappe aux quatre connus. */
  famillesInexpliquees: number;
  resteInexpliqueCents: number;
  /** Les familles dont le solde est encore provisoire. */
  famillesProvisoires: number;
}

export function bilanDesEcarts(lectures: LectureDeLEcart[]): BilanDesEcarts {
  const parMotif = new Map<CodeDeMotif, { familles: number; montantCents: number }>();
  for (const l of lectures) {
    for (const m of l.motifs) {
      const cumul = parMotif.get(m.code) ?? { familles: 0, montantCents: 0 };
      cumul.familles += 1;
      cumul.montantCents += m.montantCents;
      parMotif.set(m.code, cumul);
    }
  }

  return {
    familles: lectures.length,
    totalAbsoluCents: lectures.reduce((t, l) => t + Math.abs(l.ecartCents), 0),
    parMotif: MOTIFS.filter((code) => parMotif.has(code)).map((code) => ({
      code,
      motif: LIBELLES_DE_MOTIF[code],
      familles: parMotif.get(code)!.familles,
      montantCents: parMotif.get(code)!.montantCents,
    })),
    famillesInexpliquees: lectures.filter((l) => l.resteInexpliqueCents !== 0).length,
    resteInexpliqueCents: lectures.reduce((t, l) => t + Math.abs(l.resteInexpliqueCents), 0),
    famillesProvisoires: lectures.filter((l) => l.provisoire).length,
  };
}

/** Le nom lisible d'une famille, quel que soit ce que la base a rempli. */
export function nomDeLaFamille(faits: FaitsDeLEcart): string {
  const nom = [faits.prenom, faits.nom].filter(Boolean).join(' ').trim();
  return nom || faits.email || faits.userId;
}

function pluriel(n: number, un: string, plusieurs: string): string {
  return `${n} ${n > 1 ? plusieurs : un}`;
}

function capitaliser(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
